import type { StatusBadgeType } from '../StatusBadge/StatusBadge';

export type EntityStatusDomain = 'brand' | 'vulnerability';

export type StatusConfig = {
  variant: StatusBadgeType;
  color?: string;
  iconKey: string;
};

const BRAND_STATUSES = [
  'new',
  'assigned',
  'risk_assessment',
  'postponed',
  'rejected',
  'duplicate',
  'risk_accepted',
] as const;

export const VULNERABILITY_STATUSES = [
  'new',
  'assigned',
  'deferred',
  'rejected',
  'info',
  'duplicate',
  'fixed',
  'archived',
] as const;

const getBrandVariant = (status: string): StatusBadgeType =>
  status === 'postponed' || status === 'risk_accepted' ? 'caution' : 'neutral';

const getVulnerabilityVariant = (status: string): StatusBadgeType => {
  if (status === 'fixed') return 'positive';
  if (status === 'deferred') return 'caution';
  return 'neutral';
};

const BRAND_COLORS: Record<string, string> = {
  new: 'var(--secondary_text)',
  assigned: 'var(--secondary_text)',
  risk_assessment: 'var(--secondary_text)',
  postponed: 'var(--status-warning-fg)',
  rejected: 'var(--secondary_text)',
  duplicate: 'var(--secondary_text)',
  risk_accepted: 'var(--status-warning-fg)',
};

const VULNERABILITY_COLORS: Record<string, string> = {
  new: 'var(--secondary_text)',
  assigned: 'var(--secondary_text)',
  deferred: 'var(--status-warning-fg)',
  rejected: 'var(--secondary_text)',
  info: 'var(--secondary_text)',
  duplicate: 'var(--secondary_text)',
  fixed: 'var(--status-success-fg)',
  archived: 'var(--secondary_text)',
};

const BRAND_ICONS: Record<string, string> = {
  new: 'new',
  assigned: 'user_check',
  risk_assessment: 'progress',
  postponed: 'clock',
  rejected: 'cross',
  duplicate: 'copy',
  risk_accepted: 'check',
};

const VULNERABILITY_ICONS: Record<string, string> = {
  new: 'new',
  assigned: 'user_check',
  deferred: 'clock',
  rejected: 'cross',
  info: 'pencil_check',
  duplicate: 'copy',
  fixed: 'check',
  archived: 'info',
};

export const getStatusConfig = (domain: EntityStatusDomain, status: string): StatusConfig => {
  const effectiveStatus =
    domain === 'vulnerability' ? normalizeVulnerabilityStatus(status) : status;
  if (domain === 'brand') {
    return {
      variant: getBrandVariant(effectiveStatus),
      color: BRAND_COLORS[effectiveStatus] ?? BRAND_COLORS['new'],
      iconKey: BRAND_ICONS[effectiveStatus] ?? 'info',
    };
  }
  return {
    variant: getVulnerabilityVariant(effectiveStatus),
    color: VULNERABILITY_COLORS[effectiveStatus] ?? VULNERABILITY_COLORS['new'],
    iconKey: VULNERABILITY_ICONS[effectiveStatus] ?? 'info',
  };
};

export const getStatusesForDomain = (domain: EntityStatusDomain): readonly string[] => {
  return domain === 'brand' ? BRAND_STATUSES : VULNERABILITY_STATUSES;
};

export const getColorsForDomain = (domain: EntityStatusDomain): Record<string, string> => {
  return domain === 'brand' ? BRAND_COLORS : VULNERABILITY_COLORS;
};

export type VulnerabilityLifecycleStatus = (typeof VULNERABILITY_STATUSES)[number];

export const normalizeVulnerabilityStatus = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (['closed', 'resolved', 'resolve'].includes(normalized)) return 'fixed';
  if (['duplicates', 'duplicated'].includes(normalized)) return 'duplicate';
  return normalized;
};
